import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { Icon, StatTile, SectionHead, Panel, Empty, Pager, T, post, BackToReports } from "./SwachhataDashboard";
import { BRICS_CAMPAIGN, BRICS_DATES, BRICS_TITLE } from "../BricsCampaign/bricsConfig";
import "./SwachhataDashboard.css";
import "./BricsReport.css";

/*
 * /admin/brics-report — BRICS Sustainable Lifestyle Week (1-7 Nov 2026).
 *
 * Same drill-down as the Special Campaign 6 report: India → State/UT/AB →
 * district → block → the schools themselves. A state admin (role 4) is locked
 * to its own State/UT/AB by the server, so the breadcrumb starts one level in
 * and the state row is not clickable back to India.
 *
 * Figures come from brics/admin/report, one grouped aggregation behind a
 * shared 2-minute Mongo cache; the school list is live and paginated.
 */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

const COLUMNS = [
  { key: "weekSubmitted", label: "Schools — report form", brief: true },
  { key: "schools", label: "Schools — any BRICS entry", brief: true },
  { key: "energySubmitted", label: "Save Energy" },
  { key: "lifestyleSubmitted", label: "Healthy Lifestyle" },
  { key: "bothSubmitted", label: "Both initiatives" },
  { key: "solarYes", label: "Solar installed" },
  { key: "solarUnits", label: "Solar units generated" },
  { key: "auditYes", label: "Energy audits" },
  { key: "switchOffYes", label: "1-hour switch-off" },
  { key: "fourBinYes", label: "4-bin system" },
  { key: "segregationYes", label: "Segregation at source" },
  { key: "studentsEnergy", label: "Students (energy)" },
  { key: "studentsLifestyle", label: "Students (lifestyle)" },
  { key: "photos", label: "Photos" },
  // the 7-question School Reporting form
  { key: "weekSwitchOffYes", label: "Q1 switch-off week", brief: true },
  { key: "weekSeptReported", label: "Q2 September reported", brief: true },
  { key: "weekSeptUnits", label: "Q2 September (units)" },
  { key: "weekSeptKwh", label: "Q2 September (kWh)" },
  { key: "weekFourBinYes", label: "Q3 four-bin", brief: true },
  { key: "weekNotificationYes", label: "Q4 notification displayed", brief: true },
  { key: "weekPostersYes", label: "Q5 posters displayed", brief: true },
  { key: "weekActPosterMaking", label: "Q6 poster making", brief: true },
  { key: "weekActPainting", label: "Q6 painting competition" },
  { key: "weekActRangoli", label: "Q6 rangoli" },
  { key: "weekActSlogan", label: "Q6 slogan writing" },
  { key: "weekActOtherAct", label: "Q6 other activity" },
  { key: "weekStudents", label: "Q7 students", brief: true },
  { key: "weekTeachers", label: "Q7 teachers", brief: true },
  { key: "weekPhotos", label: "Q8 photographs", brief: true },
];

// The portal green (#23792e is its footer, nav and primary-button colour).
const BRICS_TONE = "#23792e";

// The 7-question School Reporting form, as the panel reads it out. Q2 is split
// by unit and Q6/Q7 are totals, not counts of schools, so they carry no bar.
// Q6 — the labels the form shows, so the Excel export reads in words rather
// than keys. Must match BricsCampaign/BricsWeekReportForm.jsx.
const WEEK_ACTIVITY_LABEL = {
  poster_making: "Poster Making",
  painting: "Painting Competition",
  rangoli: "Rangoli",
  slogan_writing: "Slogan Writing",
  other: "Others",
};

const WEEK_QUESTIONS = [
  { key: "weekSwitchOffYes", label: "Q1 · Held the one-hour switch-off daily for a week" },
  { key: "weekSeptReported", label: "Q2 · Reported September electricity consumption" },
  { key: "weekFourBinYes", label: "Q3 · Four-bin segregation installed" },
  { key: "weekNotificationYes", label: "Q4 · Eco Club notification on the notice board" },
  { key: "weekPostersYes", label: "Q5 · Posters and theme details on the notice board" },
  { key: "weekActPosterMaking", label: "Q6 · Poster making" },
  { key: "weekActPainting", label: "Q6 · Painting competition" },
  { key: "weekActRangoli", label: "Q6 · Rangoli" },
  { key: "weekActSlogan", label: "Q6 · Slogan writing" },
  { key: "weekActOtherAct", label: "Q6 · Other activity" },
  { key: "weekStudents", label: "Q7 · Students who took part", ofSchools: false },
  { key: "weekTeachers", label: "Q7 · Teachers who took part", ofSchools: false },
  { key: "weekPhotos", label: "Q8 · Geotagged photographs uploaded", ofSchools: false },
];

// 24 columns fit an Excel sheet, not a screen or a landscape A4 page. BRIEF is
// what the table and the PDF show; the Excel export still carries every column.
const BRIEF = COLUMNS.filter((c) => c.brief);

const LEVEL_WORD = { state: "State / UT / AB", district: "District", block: "Block" };

export default function BricsReport({ embedded = false }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState({ state: null, district: null, block: null, locked: false });
  const [level, setLevel] = useState("state");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [subThemes, setSubThemes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // school list (only once a district or block is picked)
  const [schools, setSchools] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalSchools, setTotalSchools] = useState(0);
  const [q, setQ] = useState("");
  const [schoolsBusy, setSchoolsBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [sel, setSel] = useState({ state: "", district: "", block: "" });

  const load = useCallback(async (next) => {
    setBusy(true);
    setError("");
    try {
      const d = await post("brics/admin/report", {
        state: next.state || undefined,
        district: next.district || undefined,
        block: next.block || undefined,
      });
      setRows(d.rows || []);
      setTotals(d.totals || null);
      setSubThemes(d.subThemes || null);
      setLevel(d.level || "state");
      setScope(d.scope || {});
    } catch (e) {
      setError("Could not load the BRICS report. Please try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load(sel);
  }, [sel, load]);

  const loadSchools = useCallback(async () => {
    if (!sel.district && !sel.block) return;
    setSchoolsBusy(true);
    try {
      const d = await post("brics/admin/schools", {
        state: sel.state || undefined,
        district: sel.district || undefined,
        block: sel.block || undefined,
        q: q || undefined,
        page,
        limit: 25,
      });
      setSchools(d.rows || []);
      setPages(d.pages || 1);
      setTotalSchools(d.total || 0);
    } catch (e) {
      setSchools([]);
    } finally {
      setSchoolsBusy(false);
    }
  }, [sel, q, page]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  const drill = (row) => {
    setPage(1);
    if (level === "state") setSel({ state: row.label, district: "", block: "" });
    else if (level === "district") setSel({ ...sel, district: row.label, block: "" });
    else setSel({ ...sel, block: row.label });
  };

  const crumbs = useMemo(() => {
    const c = [];
    if (!scope.locked) c.push({ label: "India", on: () => setSel({ state: "", district: "", block: "" }) });
    if (scope.state) c.push({ label: scope.state, on: () => setSel({ state: scope.state, district: "", block: "" }) });
    if (scope.district) c.push({ label: scope.district, on: () => setSel({ ...sel, block: "" }) });
    if (scope.block) c.push({ label: scope.block, on: null });
    return c;
  }, [scope, sel]);

  const exportRows = () => {
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r, i) => ({
        "S.No": i + 1,
        [LEVEL_WORD[level]]: r.label,
        ...Object.fromEntries(COLUMNS.map((c) => [c.label, r[c.key] || 0])),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BRICS");
    XLSX.writeFile(wb, `BRICS_Sustainable_Lifestyle_Week_${level}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Same figures as the table, as a document admins can circulate.
  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 36;
    const INK = [15, 36, 24];
    const HEAD = [35, 121, 46]; // the portal green
    const where = [scope.state, scope.district, scope.block].filter(Boolean).join(" › ") || "India";
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text(BRICS_CAMPAIGN, M, 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(95, 115, 104);
    doc.text(`${BRICS_TITLE} · ${BRICS_DATES} · ${LEVEL_WORD[level]}-wise · ${where}`, M, 56);
    doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, M, 70);
    const R = (v) => ({ content: v, styles: { halign: "right" } });
    doc.autoTable({
      startY: 82,
      head: [["#", LEVEL_WORD[level], ...BRIEF.map((c) => c.label)]],
      body: rows.map((r, i) => [i + 1, r.label, ...BRIEF.map((c) => fmt(r[c.key]))]),
      foot: [["", "Total", ...BRIEF.map((c) => R(fmt(t[c.key])))]],
      styles: { fontSize: 7.5, cellPadding: 3.5, lineColor: [225, 232, 228], lineWidth: 0.5, textColor: INK },
      headStyles: { fillColor: HEAD, textColor: 255, fontSize: 7.2, valign: "middle" },
      footStyles: { fillColor: [238, 240, 250], textColor: INK, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 120 } },
      alternateRowStyles: { fillColor: [250, 250, 253] },
      margin: { left: M, right: M, top: 40 },
      showFoot: "lastPage",
      didParseCell: (d) => { if (d.section === "body" && d.column.index >= 2) d.cell.styles.halign = "right"; },
    });
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFontSize(8); doc.setTextColor(120, 135, 126);
      doc.text(`Eco Clubs for Mission LiFE · ${BRICS_CAMPAIGN}`, M, H - 18);
      doc.text(`Page ${p} of ${pages}`, W - M, H - 18, { align: "right" });
    }
    doc.save(`BRICS_Sustainable_Lifestyle_Week_${level}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportSchools = async () => {
    setExporting(true);
    try {
      const d = await post("brics/admin/schools", {
        state: sel.state || undefined,
        district: sel.district || undefined,
        block: sel.block || undefined,
        export: true,
      });
      const ws = XLSX.utils.json_to_sheet(
        (d.rows || []).map((r, i) => ({
          "S.No": i + 1,
          "UDISE Code": r.udise_id,
          "School Name": r.school_name,
          State: r.state,
          District: r.district,
          Block: r.block,
          "Save Energy submitted": r.saveEnergy?.status === "submitted" ? "Yes" : "No",
          "Solar panel installed": r.saveEnergy?.solarPanelInstalled || "",
          "Solar units generated": r.saveEnergy?.solarUnitsGenerated || 0,
          "Bill reduction": r.saveEnergy?.reductionPct ? `${r.saveEnergy.reductionPct}%` : "",
          "Energy audit done": r.saveEnergy?.energyAuditDone || "",
          "1-hour switch-off": r.saveEnergy?.oneHourSwitchOffObserved || "",
          "Students (energy)": r.saveEnergy?.studentsInvolved || 0,
          "Healthy Lifestyle submitted": r.adoptHealthyLifestyle?.status === "submitted" ? "Yes" : "No",
          "4-bin system installed": r.adoptHealthyLifestyle?.fourBinSystemInstalled || "",
          "Segregation at source": r.adoptHealthyLifestyle?.wasteSegregatedAtSource || "",
          "Students (lifestyle)": r.adoptHealthyLifestyle?.studentsSensitised || 0,
          // the 7-question School Reporting form
          "Report form submitted": r.weekReport?.status === "submitted" ? "Yes" : "No",
          "Q1 switch-off week": r.weekReport?.switchOffWeekObserved || "",
          "Q2 September consumption": r.weekReport?.septemberConsumption ?? "",
          "Q2 unit": r.weekReport?.consumptionUnit === "kwh" ? "kWh" : r.weekReport?.consumptionUnit ? "Units" : "",
          "Q3 four-bin installed": r.weekReport?.fourBinInstalled || "",
          "Q3 bins": (r.weekReport?.bins || []).map((b) => `${b.colour}: ${b.category}`).join("; "),
          "Q4 notification displayed": r.weekReport?.notificationDisplayed || "",
          "Q4 notification file": r.weekReport?.notificationFile || "",
          "Q5 posters displayed": r.weekReport?.postersDisplayed || "",
          "Q6 activities": (r.weekReport?.activities || []).map((k) => WEEK_ACTIVITY_LABEL[k] || k).join("; "),
          "Q6 other activity": r.weekReport?.activitiesOther || "",
          "Q7 students": r.weekReport?.studentsParticipated ?? "",
          "Q7 teachers": r.weekReport?.teachersParticipated ?? "",
          "Q8 photographs": (r.weekReport?.photos || []).length,
        }))
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Schools");
      XLSX.writeFile(wb, `BRICS_Schools_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const t = totals || {};
  const showSchools = !!(sel.district || sel.block);

  // Embedded in the admin shell the page already carries a hero naming this
  // report, so only the actions are rendered — two stacked banners read badly.
  const actions = (
    <>
      <button type="button" className="swx-btn swx-btn--soft" onClick={exportRows} disabled={!rows.length}>
        <Icon name="download" size={16} />
        Excel
      </button>
      <button type="button" className="swx-btn swx-btn--soft" onClick={exportPdf} disabled={!rows.length}>
        <Icon name="download" size={16} />
        PDF
      </button>
      {embedded && (
        <button type="button" className="swx-btn swx-btn--glass" onClick={() => navigate("/admin/brics-report")}>
          <Icon name="report" size={16} />
          Open full report
        </button>
      )}
    </>
  );

  return (
    <div className={`swx brics-rep${embedded ? " is-embedded" : ""}`}>
      <div className="swx-wrap">
      {/* Inside the dashboard shell this is a panel with the rail beside it,
          so the way back is already there; on its own page it is not. */}
      {!embedded && <BackToReports />}
      {embedded ? (
        <div className="brics-toolbar">{actions}</div>
      ) : (
        <SectionHead
          tone={BRICS_TONE}
          icon="report"
          eyebrow={BRICS_DATES}
          title={BRICS_CAMPAIGN}
          desc={`${BRICS_TITLE} — ${scope.locked ? "your State / UT / AB" : "India"}, drill down to district, block and school. Figures refresh at most every 2 minutes; the school list below is live.`}
          actions={actions}
        />
      )}

      <nav className="swx-crumbs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => (
          <span key={`${c.label}-${i}`}>
            {i > 0 && <span className="swx-crumbs__sep"> / </span>}
            {c.on ? (
              <button type="button" className="swx-crumbs__link" onClick={c.on}>
                {c.label}
              </button>
            ) : (
              <strong>{c.label}</strong>
            )}
          </span>
        ))}
      </nav>

        <section className="swx-stats">
        <StatTile i={0} icon="report" tone={BRICS_TONE} loading={busy} label="Schools that sent the report form" value={fmt(t.weekSubmitted)} sub="All 8 questions answered" />
        <StatTile i={1} icon="school" tone={T.orange} loading={busy} label="Schools with any BRICS entry" value={fmt(t.schools)} sub={`includes ${fmt(Math.max(0, (t.schools || 0) - (t.weekSubmitted || 0)))} that sent only the older initiative forms`} />
        <StatTile i={2} icon="users" tone={T.green} loading={busy} label="Participation" value={fmt((t.weekStudents || 0) + (t.weekTeachers || 0))} sub={`${fmt(t.weekStudents)} students · ${fmt(t.weekTeachers)} teachers`} />
        <StatTile i={3} icon="leaf" tone={T.blue} loading={busy} label="Notice board" value={fmt(t.weekPostersYes)} sub={`${fmt(t.weekNotificationYes)} displayed the notification`} />
        </section>

      {error && <div className="swx-panel" style={{ padding: 16, color: "#b3261e", fontWeight: 600 }}>{error}</div>}

      {/* The seven questions, answered. Every figure comes from the same cached
          aggregation as the table, so this panel costs no extra query. */}
      <Panel
        title="School Reporting — question by question"
        desc={`Out of ${fmt(t.weekSubmitted)} schools that sent the 8-question report form in this area.`}
      >
        {!t.weekSubmitted ? (
          <Empty text="No school has sent the report form in this area yet." />
        ) : (
          <div className="brics-sub">
            <div className="brics-sub__col">
              {WEEK_QUESTIONS.slice(0, Math.ceil(WEEK_QUESTIONS.length / 2)).map((q) => (
                <div className="brics-sub__row" key={q.key}>
                  <div className="brics-sub__name">
                    {q.label}
                    <div className="brics-sub__bar"><i style={{ width: `${Math.max(4, pct(t[q.key], t.weekSubmitted))}%` }} /></div>
                  </div>
                  <span className="brics-sub__n">{fmt(t[q.key])}</span>
                </div>
              ))}
            </div>
            <div className="brics-sub__col">
              {WEEK_QUESTIONS.slice(Math.ceil(WEEK_QUESTIONS.length / 2)).map((q) => (
                <div className="brics-sub__row" key={q.key}>
                  <div className="brics-sub__name">
                    {q.label}
                    {q.ofSchools !== false && (
                      <div className="brics-sub__bar"><i style={{ width: `${Math.max(4, pct(t[q.key], t.weekSubmitted))}%` }} /></div>
                    )}
                  </div>
                  <span className="brics-sub__n">{fmt(t[q.key])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {subThemes && (subThemes.saveEnergy?.length || subThemes.adoptHealthyLifestyle?.length) ? (
        <Panel
          title="What schools recorded"
          desc="The Mission LiFE sub-theme each school chose, within the current scope."
        >
          <div className="brics-sub">
            {[
              ["Save Energy", subThemes.saveEnergy || []],
              ["Adopt Healthy Lifestyle", subThemes.adoptHealthyLifestyle || []],
            ].map(([heading, list]) => {
              const top = list[0]?.schools || 1;
              return (
                <div className="brics-sub__col" key={heading}>
                  <h5>{heading}</h5>
                  {!list.length && <Empty text="Nothing recorded yet." />}
                  {list.map((r) => (
                    <div className="brics-sub__row" key={r.label}>
                      <div className="brics-sub__name">
                        {r.label}
                        <div className="brics-sub__bar"><i style={{ width: `${Math.max(4, pct(r.schools, top))}%` }} /></div>
                      </div>
                      <span className="brics-sub__n">{fmt(r.schools)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </Panel>
      ) : null}

      <Panel
        title={`${LEVEL_WORD[level]}-wise participation`}
        desc={level === "block" ? "Pick a block to see its schools." : "Select a row to go one level deeper."}
      >
        {!rows.length && !busy ? (
          <Empty text="No BRICS reports in this area yet." />
        ) : (
          <div className="swx-table-wrap">
            <table className="swx-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{LEVEL_WORD[level]}</th>
                  {BRIEF.map((c) => (
                    <th key={c.key} className="num">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key || r.label} onClick={() => drill(r)} style={{ cursor: "pointer" }}>
                    <td>{i + 1}</td>
                    <td>{r.label}</td>
                    {BRIEF.map((c) => (
                      <td key={c.key} className="num">
                        {fmt(r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td />
                  <td>Total</td>
                  {BRIEF.map((c) => (
                    <td key={c.key} className="num">
                      {fmt(t[c.key])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>

      {showSchools && (
        <Panel
          title="Schools"
          desc={`${fmt(totalSchools)} school${totalSchools === 1 ? "" : "s"} reported in this area.`}
          right={
            <div className="swx-panel__tools">
              <div className="swx-search">
                <Icon name="search" size={16} />
                <input
                  type="search"
                  placeholder="Search name or UDISE…"
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                />
              </div>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportSchools} disabled={exporting || !totalSchools}>
                <Icon name="download" size={16} />
                {exporting ? "Preparing…" : "Excel"}
              </button>
            </div>
          }
        >
          {!schools.length && !schoolsBusy ? (
            <Empty text="No schools match this filter." />
          ) : (
            <>
              <div className="swx-table-wrap">
                <table className="swx-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>UDISE</th>
                      <th>School</th>
                      <th>Block</th>
                      <th>Report form</th>
                      <th>Save Energy</th>
                      <th>Healthy Lifestyle</th>
                      <th className="num">Q2 September</th>
                      <th className="num">Q7 participation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((s, i) => (
                      <tr key={s.udise_id}>
                        <td>{(page - 1) * 25 + i + 1}</td>
                        <td>{s.udise_id}</td>
                        <td>{s.school_name}</td>
                        <td>{s.block}</td>
                        <td>{s.weekReport?.status === "submitted" ? "Yes" : "—"}</td>
                        <td>{s.saveEnergy?.status === "submitted" ? "Yes" : "—"}</td>
                        <td>{s.adoptHealthyLifestyle?.status === "submitted" ? "Yes" : "—"}</td>
                        <td className="num">
                          {s.weekReport?.septemberConsumption == null
                            ? "—"
                            : `${fmt(s.weekReport.septemberConsumption)} ${s.weekReport.consumptionUnit === "kwh" ? "kWh" : "units"}`}
                        </td>
                        <td className="num">
                          {fmt((s.weekReport?.studentsParticipated || 0) + (s.weekReport?.teachersParticipated || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={page} totalPages={pages} total={totalSchools} noun="schools" onPage={setPage} loading={schoolsBusy} />
            </>
          )}
        </Panel>
      )}
      </div>
    </div>
  );
}
